import React, { useState } from 'react';
import axios from 'axios';
import { Sparkles, Search, AlertCircle, ExternalLink, Lightbulb, CheckCircle } from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import ToolCard from '../components/ToolCard';
import { toast } from 'sonner';
import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AISearchPage = () => {
  const [problem, setProblem] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!problem.trim()) {
      toast.error('Please describe your problem');
      return;
    }

    setSearching(true);
    setResults(null);

    try {
      const response = await axios.post(`${API}/tools/ai-search`, {
        usecase: problem
      });

      setResults(response.data);
      
      if (!response.data.found_solutions || response.data.grouped_tools.length === 0) {
        toast.info('No exact matches found in database. Showing suggestions from the web.');
      } else {
        const totalTools = response.data.grouped_tools.reduce((sum, group) => sum + group.tools.length, 0);
        toast.success(`Found ${totalTools} solutions across ${response.data.grouped_tools.length} categories!`);
      }
    } catch (error) {
      console.error('Error searching:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const problemExamples = [
    'Need comprehensive AWS cost monitoring with multi-account budget alerts and optimization recommendations',
    'Looking to implement security scanning and vulnerability detection for production Kubernetes clusters',
    'Require infrastructure-as-code solution for multi-cloud deployment automation with state management',
    'Need distributed tracing and observability platform for debugging microservices architecture',
    'Looking for enterprise-grade secrets management with rotation and audit capabilities',
    'Want to establish GitOps-based continuous deployment pipeline for containerized workloads'
  ];

  return (
    <div className="min-h-screen px-6 py-12">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 backdrop-blur-sm mb-6">
            <Sparkles className="w-4 h-4 text-purple-400 animate-pulse" />
            <span className="text-sm text-purple-300 font-medium">Intelligent Tool Recommendation Engine</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="gradient-text">AI Tool Advisor</span>
          </h1>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Describe your infrastructure challenge or use case, and our AI will intelligently analyze your requirements 
            to recommend the most relevant tools from our curated directory, organized by solution categories.
          </p>
        </div>

        {/* Search Form */}
        <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-8 mb-8">
          <form onSubmit={handleSearch} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Describe your infrastructure challenge or requirement
              </label>
              <Textarea
                data-testid="problem-input"
                value={problem}
                onChange={(e) => setProblem(e.target.value)}
                placeholder="e.g., Need a solution to monitor AWS costs in real-time and trigger alerts when spending exceeds predefined budgets across multiple accounts"
                rows={4}
                className="bg-gray-900/50 border-gray-600 text-white placeholder-gray-500 focus:border-purple-500 focus:ring-purple-500/20"
              />
            </div>

            <button
              data-testid="problem-search-btn"
              type="submit"
              disabled={searching}
              className="w-full inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-semibold hover:from-purple-600 hover:to-blue-600 transition-all shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {searching ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Analyzing Requirements...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Get AI Recommendations
                </>
              )}
            </button>
          </form>

          {/* Example Problems */}
          <div className="mt-6">
            <p className="text-sm text-gray-400 mb-3 flex items-center gap-2">
              <Lightbulb className="w-4 h-4" />
              Common use cases to explore:
            </p>
            <div className="grid sm:grid-cols-2 gap-2">
              {problemExamples.map((example, index) => (
                <button
                  key={index}
                  data-testid={`example-${index}`}
                  onClick={() => setProblem(example)}
                  className="text-left text-sm px-4 py-3 rounded-lg bg-gradient-to-br from-gray-800/50 to-gray-700/30 hover:from-purple-500/10 hover:to-blue-500/10 text-gray-300 transition-all border border-gray-600/50 hover:border-purple-500/40 hover:shadow-md"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="space-y-8 fade-in">
            {/* Problem Analysis */}
            {results.problem_analysis && (
              <div className="bg-gradient-to-r from-purple-500/10 via-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-6 shadow-lg">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-purple-300 mb-2">AI Analysis</h3>
                    <p className="text-gray-300 leading-relaxed">{results.problem_analysis}</p>
                    
                    {results.solution_categories && results.solution_categories.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-gray-400 mb-2 font-medium">Identified Solution Categories:</p>
                        <div className="flex flex-wrap gap-2">
                          {results.solution_categories.map((cat, idx) => (
                            <span
                              key={idx}
                              className="px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-purple-300 text-sm border border-purple-500/40 font-medium"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Grouped Solutions from Database */}
            {results.found_solutions && results.grouped_tools && results.grouped_tools.length > 0 && (
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <h2 className="text-3xl font-bold">Recommended Tools</h2>
                  <span className="px-3 py-1.5 rounded-full bg-gradient-to-r from-purple-500/20 to-blue-500/20 border border-purple-500/30 text-purple-300 text-sm font-semibold">
                    {results.grouped_tools.reduce((sum, group) => sum + group.tools.length, 0)} matches
                  </span>
                </div>
                
                <div className="space-y-8">
                  {results.grouped_tools.map((categoryGroup, catIndex) => (
                    <div key={catIndex} className="space-y-4">
                      {/* Category Header */}
                      <div className="flex items-center gap-3">
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                        <h3 className="text-xl font-bold text-blue-400">
                          {categoryGroup.category}
                        </h3>
                        <span className="px-2 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs">
                          {categoryGroup.tools.length}
                        </span>
                        <div className="h-px flex-1 bg-gradient-to-r from-transparent via-gray-700 to-transparent"></div>
                      </div>
                      
                      {/* Tools in Category */}
                      <div className="grid md:grid-cols-2 gap-6">
                        {categoryGroup.tools.map((toolWithExplanation, toolIndex) => (
                          <div 
                            key={toolWithExplanation.tool.id} 
                            className="slide-in" 
                            style={{ animationDelay: `${(catIndex * 2 + toolIndex) * 0.1}s` }}
                          >
                            <div className="space-y-3">
                              <ToolCard tool={toolWithExplanation.tool} />
                              
                              {/* Solution Explanation */}
                              <div className="bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-blue-500/30 rounded-xl p-4 shadow-sm">
                                <div className="flex items-start gap-2">
                                  <Lightbulb className="w-4 h-4 text-blue-400 mt-1 flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-sm font-semibold text-blue-300 mb-1">Why this tool fits:</p>
                                    <p className="text-sm text-gray-300 leading-relaxed">
                                      {toolWithExplanation.explanation}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Web Suggestions (No DB matches) */}
            {(!results.found_solutions || results.grouped_tools.length === 0) && results.web_suggestions && (
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                  <h2 className="text-3xl font-bold">Suggested Solutions from the Web</h2>
                </div>
                
                <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-purple-500/20 p-8">
                  <div className="flex items-start gap-3 mb-4">
                    <AlertCircle className="w-5 h-5 text-purple-400 mt-1 flex-shrink-0" />
                    <p className="text-sm text-gray-400">
                      No exact matches found in our database. Here are popular tools from the web that could solve your problem:
                    </p>
                  </div>
                  
                  <div className="bg-gray-900/50 rounded-xl p-6 whitespace-pre-wrap text-gray-300 leading-relaxed">
                    {results.web_suggestions}
                  </div>
                  
                  <div className="mt-6 flex gap-4">
                    <button
                      data-testid="add-tool-from-suggestion-btn"
                      onClick={() => window.location.href = '/add-tool'}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Add Tool to Database
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AISearchPage;